import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2, FiMail, FiPrinter, FiDownload, FiSave } from 'react-icons/fi';
import RichTextEditor from '../common/RichTextEditor';
import { CURRENCIES, DEFAULT_CURRENCY, formatCurrency, getCurrencyOptions } from '../../utils/currency';

export default function InvoiceModal({ appointment, onClose, onSave }) {
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    clientName: '',
    clientEmail: '',
    appointmentType: '',
    appointmentDuration: '',
    appointmentDate: '',
    rate: '',
    currency: DEFAULT_CURRENCY,
    lineItems: [
      {
        id: 1,
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0
      }
    ],
    discount: 0,
    tax: 0,
    subtotal: 0,
    total: 0,
    status: 'unpaid',
    notes: '',
    manualTotal: false,
    manualTotalValue: 0
  });

  const [clinicSettings, setClinicSettings] = useState({});

  useEffect(() => {
    const loadInvoiceData = async () => {
      try {
        // Load clinic settings from Firebase
        const { settingsService } = await import('../../services/settingsService');
        const settings = await settingsService.getSettings();
        console.log('🔍 InvoiceModal - Loaded settings:', settings);
        console.log('🔍 InvoiceModal - Settings treatmentRates:', settings?.treatmentRates);
        setClinicSettings(settings);

        // Generate invoice number using Firebase service
        const { invoiceService } = await import('../../services/invoiceService');
        const invoiceNumber = await invoiceService.getNextInvoiceNumber();

        // Prefill from appointment if provided
        if (appointment) {
          const client = appointment.client || {};
          // Find treatment type from settings - check treatmentRates first, then fallback to treatmentTypes
          let hourlyRate = 150;
          let duration = appointment.duration || 60;
          let pricingType = 'hourly';
          
          // First check treatmentRates (AdminSettings format)
          const treatmentRate = settings.treatmentRates?.[appointment.type];
          if (treatmentRate) {
            hourlyRate = treatmentRate.value || 150;
            duration = treatmentRate.duration || appointment.duration || 60;
            pricingType = treatmentRate.type || 'hourly';
            console.log('🔍 InvoiceModal - Using treatmentRates:', treatmentRate);
          } else {
            // Fallback to treatmentTypes (old format)
            const treatmentType = settings.treatmentTypes?.find(t => t.name === appointment.type);
            if (treatmentType) {
              hourlyRate = treatmentType.value || 150;
              pricingType = treatmentType.pricingType || 'hourly';
              console.log('🔍 InvoiceModal - Using treatmentTypes fallback:', treatmentType);
            } else {
              console.log('🔍 InvoiceModal - No treatment data found, using defaults');
            }
          }
          
          const currencySymbol = settings.currencySymbol || '£';
          console.log('🔍 InvoiceModal - Final values - hourlyRate:', hourlyRate, 'duration:', duration, 'type:', appointment.type);
          
          const mainService = pricingType === 'flat' 
            ? `${appointment.type} – ${currencySymbol}${hourlyRate} (flat rate)`
            : `${appointment.type} – ${duration}min @ ${currencySymbol}${hourlyRate}/hr`;
          const amount = pricingType === 'flat' 
            ? hourlyRate 
            : (duration / 60) * hourlyRate;

          setFormData(prev => ({
            ...prev,
            invoiceNumber,
            clientName: client.name || '',
            clientEmail: client.email || '',
            clientAddress: client.address || '',
            currency: settings.currency || DEFAULT_CURRENCY,
            lineItems: [{
              id: 1,
              description: mainService,
              amount: amount
            }],
            subtotal: amount,
            total: amount
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            invoiceNumber,
            currency: settings.currency || DEFAULT_CURRENCY
          }));
        }
      } catch (error) {
        console.error('Error loading invoice data:', error);
        // Fallback to basic invoice number
        const fallbackNumber = `INV-${Date.now().toString().slice(-7)}`;
        setFormData(prev => ({
          ...prev,
          invoiceNumber: fallbackNumber
        }));
      }
    };

    loadInvoiceData();
  }, [appointment]);

  useEffect(() => {
    calculateTotals();
  }, [formData.lineItems, formData.discount, formData.tax]);

  const calculateTotals = () => {
    const subtotal = parseFloat(formData.lineItems.reduce((sum, item) => sum + (item.amount || 0), 0).toFixed(2));
    const discountAmount = parseFloat(((subtotal * formData.discount) / 100).toFixed(2));
    const taxableAmount = parseFloat((subtotal - discountAmount).toFixed(2));
    const taxAmount = parseFloat(((taxableAmount * formData.tax) / 100).toFixed(2));
    const total = parseFloat((taxableAmount + taxAmount).toFixed(2));

    setFormData(prev => ({
      ...prev,
      subtotal,
      discountAmount,
      taxAmount,
      total: prev.manualTotal ? prev.manualTotalValue : total
    }));
  };

  const addLineItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0
    };
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, newItem]
    }));
  };

  const updateLineItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updated.amount = updated.quantity * updated.rate;
          }
          return updated;
        }
        return item;
      })
    }));
  };

  const removeLineItem = (id) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter(item => item.id !== id)
    }));
  };

  const handleSave = async () => {
    try {
      const newInvoice = {
        ...formData,
        id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      appointmentId: appointment?.id || null
      };
      
      // Save to Firebase instead of localStorage
      const { invoiceService } = await import('../../services/invoiceService');
      const savedInvoice = await invoiceService.createInvoice(newInvoice);
      
      if (onSave) onSave(savedInvoice);
      onClose();
    } catch (error) {
      console.error('Error saving invoice:', error);
    }
  };

  const handleMarkAsPaid = () => {
    setFormData(prev => ({
      ...prev,
      status: prev.status === 'paid' ? 'unpaid' : 'paid'
    }));
  };

  const handleEmailToClient = () => {
    // In a real app, this would integrate with an email service
    alert(`Invoice ${formData.invoiceNumber} would be emailed to ${formData.clientEmail}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    // In a real app, this would generate a PDF
    alert('PDF export functionality would be implemented here');
  };

  const handleExportCSV = () => {
    // In a real app, this would generate a CSV
    alert('CSV export functionality would be implemented here');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {appointment ? 'Create Invoice' : 'New Invoice'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Invoice Header */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={formData.appointmentDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => setFormData(prev => ({ ...prev, appointmentDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {getCurrencyOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Client Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Email</label>
              <input
                type="email"
                value={formData.clientEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, clientEmail: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
              <button
                onClick={addLineItem}
                className="flex items-center space-x-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <FiPlus size={16} />
                <span>Add Item</span>
              </button>
            </div>

            <div className="space-y-3">
              {formData.lineItems.map((item, index) => (
                <div key={item.id} className="grid grid-cols-12 gap-3 items-center p-3 bg-gray-50 rounded-lg">
                  <div className="col-span-5">
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Rate"
                      value={item.rate}
                      onChange={(e) => updateLineItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={item.amount}
                      onChange={(e) => updateLineItem(item.id, 'amount', parseFloat(e.target.value) || 0)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="col-span-1">
                    {formData.lineItems.length > 1 && (
                      <button
                        onClick={() => removeLineItem(item.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-gray-200 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount (%)</label>
                  <input
                    type="number"
                    value={formData.discount === 0 ? '' : formData.discount}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tax/VAT (%)</label>
                  <input
                    type="number"
                    value={formData.tax === 0 ? '' : formData.tax}
                    onChange={(e) => setFormData(prev => ({ ...prev, tax: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="0"
                    step="0.01"
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(formData.subtotal, formData.currency)}</span>
                </div>
                {formData.discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount ({formData.discount}%):</span>
                    <span className="font-medium text-red-600">-{formatCurrency((formData.subtotal * formData.discount) / 100, formData.currency)}</span>
                  </div>
                )}
                {formData.tax > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax ({formData.tax}%):</span>
                    <span className="font-medium">{formatCurrency(((formData.subtotal - (formData.subtotal * formData.discount) / 100) * formData.tax) / 100, formData.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(formData.total, formData.currency)}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="manualTotal"
                    checked={formData.manualTotal}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      manualTotal: e.target.checked,
                      manualTotalValue: e.target.checked ? prev.total : 0
                    }))}
                    className="rounded"
                  />
                  <label htmlFor="manualTotal" className="text-sm text-gray-600">Override total manually</label>
                </div>
                
                {formData.manualTotal && (
                  <div>
                    <input
                      type="number"
                      value={formData.manualTotalValue}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value) || 0;
                        const calculatedTotal = formData.subtotal - (formData.subtotal * formData.discount / 100) + (((formData.subtotal - (formData.subtotal * formData.discount / 100)) * formData.tax) / 100);
                        if (newValue <= calculatedTotal) {
                          setFormData(prev => ({ 
                            ...prev, 
                            manualTotalValue: newValue,
                            total: newValue
                          }));
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      step="0.01"
                      max={formData.subtotal - (formData.subtotal * formData.discount / 100) + (((formData.subtotal - (formData.subtotal * formData.discount / 100)) * formData.tax) / 100)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum: {formatCurrency(formData.subtotal - (formData.subtotal * formData.discount / 100) + (((formData.subtotal - (formData.subtotal * formData.discount / 100)) * formData.tax) / 100), formData.currency)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <RichTextEditor
              value={formData.notes}
              onChange={(value) => setFormData(prev => ({ ...prev, notes: value }))}
              placeholder="Additional notes or terms..."
              minHeight={100}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-6 border-t border-gray-200">
            <button
              onClick={handleMarkAsPaid}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                formData.status === 'paid'
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>{formData.status === 'paid' ? 'Mark as Unpaid' : 'Mark as Paid'}</span>
            </button>
            
            <button
              onClick={handleEmailToClient}
              disabled={!formData.clientEmail}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiMail size={16} />
              <span>Email to Client</span>
            </button>
            
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FiPrinter size={16} />
              <span>Print</span>
            </button>
            
            <button
              onClick={handleExportPDF}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FiDownload size={16} />
              <span>Save PDF</span>
            </button>
            
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FiDownload size={16} />
              <span>Export CSV</span>
            </button>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiSave size={16} />
              <span>Save Invoice</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
